import React, { useEffect } from "react";
import { X, AlertTriangle, Loader2, Info } from "lucide-react";

/**
 * Replaces window.confirm(). tone: "primary" (gold button) or "danger" (red button).
 */
const PurchaseConfirmDialog = ({
  open,
  title,
  message,
  details,
  confirmLabel = "Confirm",
  tone = "primary",
  loading = false,
  onConfirm,
  onCancel,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, loading, onCancel]);

  if (!open) return null;
  const danger = tone === "danger";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && !loading && onCancel()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="purchase-confirm-title"
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
      >
        <header className="flex items-start justify-between gap-4 px-6 py-4 bg-slate-50 border-b border-slate-200">
          <h2 id="purchase-confirm-title" className="text-lg font-bold text-white">
            {title}
          </h2>
          <button
            onClick={onCancel}
            disabled={loading}
            aria-label="Close"
            className="p-1.5 -mr-1.5 rounded-lg text-slate-500 hover:bg-slate-200/70 hover:text-slate-800 disabled:opacity-50 transition-colors"
          >
            <X size={20} />
          </button>
        </header>

        <div className="px-6 py-5 space-y-3 text-sm text-slate-700">
          {message && <p>{message}</p>}
          {details && (
            <div
              className={`flex gap-2.5 rounded-lg border px-4 py-3 ${danger ? "bg-red-50 border-red-200 text-red-800" : "bg-sky-50 border-sky-200 text-sky-800"
                }`}
            >
              {danger ? (
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              ) : (
                <Info size={16} className="mt-0.5 shrink-0" />
              )}
              <p>{details}</p>
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-200">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-60 transition-colors"
          >
            Keep as is
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium shadow-sm border transition-colors disabled:opacity-60 ${danger
                ? "text-white bg-red-600 border-red-700 hover:bg-red-700"
                : "text-white border-transparent bg-[#b07d17] hover:bg-[#8c6312]"
              }`}
          >
            {loading && <Loader2 size={15} className="animate-spin" />}
            {loading ? "Please wait…" : confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default PurchaseConfirmDialog;